import { create } from 'zustand'
import { deletionRequestsApi, type PendingDeletion } from '@/lib/api'
import { toast } from 'sonner'

/**
 * Approver queue for a Representative's deletion requests (contacts or a
 * contact list). Mirrors crmExportApprovalsStore: a Rep may add contacts to
 * any team list and take them off, but deleting needs a Manager/Admin/Owner
 * to agree -- approving performs the deletion right away.
 */
interface DeletionApprovalsStore {
  pending: PendingDeletion[]
  loading: boolean

  fetchPending: () => Promise<void>
  approve: (requestId: number) => Promise<void>
  reject: (requestId: number, reason?: string) => Promise<void>
  reset: () => void
}

const initialState = {
  pending: [] as PendingDeletion[],
  loading: false,
}

export const useDeletionApprovalsStore = create<DeletionApprovalsStore>((set, get) => ({
  ...initialState,

  fetchPending: async () => {
    set({ loading: true })
    try {
      const response = await deletionRequestsApi.list()
      set({ pending: response.data, loading: false })
    } catch {
      // 403 for any role other than Owner/Admin/Manager -- the section checks
      // usePermission before calling this, so a failure here is unusual.
      set({ loading: false })
    }
  },

  approve: async (requestId: number) => {
    try {
      const response = await deletionRequestsApi.approve(requestId)
      set({ pending: get().pending.filter((p) => p.id !== requestId) })
      // Approving IS the deletion -- report what actually happened. Contacts
      // still in an active campaign are skipped server-side, same as a direct
      // delete, and that is a normal outcome worth saying.
      const r = response.data
      if (r.kind === 'list') {
        toast.success(`Deleted list "${r.summary}"`)
      } else {
        const parts = [`Deleted ${r.deleted_count} contact${r.deleted_count === 1 ? '' : 's'}`]
        if (r.skipped_in_active_campaigns > 0) parts.push(`${r.skipped_in_active_campaigns} skipped (in active campaigns)`)
        if (r.skipped_in_active_campaigns > 0) toast.warning(parts.join(', '))
        else toast.success(parts.join(', '))
      }
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to approve the deletion')
      throw error
    }
  },

  reject: async (requestId: number, reason?: string) => {
    try {
      await deletionRequestsApi.reject(requestId, reason)
      set({ pending: get().pending.filter((p) => p.id !== requestId) })
      toast.success('Deletion request rejected')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to reject the deletion')
      throw error
    }
  },

  reset: () => set(initialState),
}))
